/**
 * What an agent cost (docs/efficiency-plan.md): the panel after a metered
 * pass (components/play/WhatItCost.tsx), the line on the public page
 * (components/profile/PublicProfile.tsx), the techniques page and the
 * study's page (components/efficiency/).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why.
 *
 * Three rules hold for every language here, and they are the plan's:
 *
 * - **No savings figure, anywhere, implied or stated.** Until the study
 *   reports, nothing says the course or a habit saves a share of anything.
 *   A habit is said with its reason, never with a number.
 * - **Par is ours and measured.** It is "what the cheapest model that passed every lab run paid to pass",
 *   at a fixed price book. Never "the right cost" or a target to beat.
 * - **The study's arm is never named.** The study page says that the timing
 *   of feedback differs between people, at random, because consent needs it;
 *   nothing tells anyone which way it went for them.
 *
 * Glossary: par is المرجع, Par, パー. The price book is جدول الأسعار,
 * Preisbuch, 価格表. Model names and the price-book edition are machine text
 * and stay Latin, isolated with `ltr()` inside Arabic.
 */
import type { AttemptKind, AttemptOutcome, BenchTaskState, Technique } from '@engineer-os/engine';
import { calendarDate, ltr, num } from '../format';

/** Why an enrolment was refused (worker/study.ts). */
export type StudyRefusal = 'closed' | 'no-ai-key' | 'no-course' | 'no-ai-credits' | 'enrolled' | 'consent';

/** The four benchmark kinds, in the forms' order (BENCH_FORMS). */
type BenchKinds = [string, string, string, string];

interface Lesson {
  name: string;
  /** One line, under the name on the clear. */
  line: string;
  /** The teaching text, on the techniques page. */
  body: string;
}

/* ── English ──────────────────────────────────────────────────────────────── */

export const en = {
  under: (cent: string) => `under ${cent}`,
  ratio: (x: string) => `${x}× par`,
  calls: (n: number) => (n === 1 ? '1 model call' : `${num(n, 'en')} model calls`),

  /* --- After a metered pass (components/play/WhatItCost.tsx) ------------- */
  clear: {
    heading: 'What it cost',
    yours: 'Your agent',
    par: 'The right model',
    noPar: 'Par for this one is not measured yet.',
    reported: 'Counted from what your agent’s tool reported.',
    costliest: 'Costliest calls',
    tokens: (input: number, output: number) => `${num(input, 'en')} tokens in, ${num(output, 'en')} out`,
    tryThis: 'One thing to try',
    how: 'How',
    /** With right-size-model: the model par was measured on, which passed every lab run of this one. */
    tryModel: (model: string) => `For this one, try ${ltr(model)}: it passed every one of our lab runs.`,
  },

  /* --- The habits (docs/efficiency-techniques.md) ------------------------ */
  techniques: {
    'scope-context': {
      name: 'Scope the context',
      line: 'Name the files the agent needs, rather than letting it read the whole repository.',
      body: 'Every file the agent reads goes into its context, and every later turn reads that context again. Tell it which files matter and what to leave alone. A short, named scope costs less on every turn that follows.',
    },
    'right-size-model': {
      name: 'Choose the smallest model that works',
      line: 'Start on the smallest model, and move up only when it fails.',
      body: 'Our Claude and Codex lab comparisons found that a smaller model could reach the same correct result for less. Begin with the smallest model suited to your task and check its work. If it fails or goes in circles, move up for that step, then return to the smaller model. Measure the whole route to a correct result, including failed attempts; your own task may need a different model.',
    },
    restart: {
      name: 'Restart instead of arguing',
      line: 'After a wrong turn, clear the session and start again from a short summary.',
      body: 'Context grows with every turn, and every turn reads all of it again. Correcting an agent five times means paying for the mistake five times. Write down in two lines what you know now, start a fresh session, and give it those.',
    },
    'exact-bug-report': {
      name: 'Report bugs exactly',
      line: 'Give the symptom, the steps and what you expected.',
      body: '“It is broken” sends the agent searching the whole project. The exact error, the steps that cause it and what should have happened instead let it go straight to the cause.',
    },
    'stable-prefix': {
      name: 'Keep the prefix stable',
      line: 'Keep the start of the conversation the same, so prompt caching can work.',
      body: 'Providers charge less for input they have already seen at the start of a conversation. Instructions that change on every turn break that cache. Put what stays the same first, and leave it alone.',
    },
  } as Record<Technique, Lesson>,

  /* --- The techniques page, /efficiency ---------------------------------- */
  page: {
    docTitle: 'What an agent costs · EngineerOS',
    kicker: 'Efficiency',
    title: 'What an agent costs, and the habits that change it',
    lede: 'After a pass with your agent, the level shows what it cost against par and points to the one habit below that your agent’s calls suggest. The first is the one our lab measured: the smallest model that still does the job. Each habit stays on this list only while our own lab runs support it.',
    parTitle: 'What par means',
    parBody: (book: string) =>
      `Par is what the cheapest model that passed every one of our lab runs paid to pass the same level, checkpoint or task, measured by us. Every cost is priced with a fixed price book, ${book}, so a provider changing its prices never moves the comparison. 1.0× is par.`,
    hand: 'Only a pass is compared with par. Work done by hand is always allowed, and has no cost figure.',
    private: 'Costs are counted at our own gateway, from the model, the token counts and the price. The text of your prompts and of the answers is never stored.',
    listTitle: 'The habits',
    back: 'Back to the course',
  },

  /* --- The label the clear panel shares (components/play/WhatItCost.tsx). The
     task board's costs panel that held the rest retired with paid tasks. --- */
  costs: {
    vsPar: 'Against par',
  },
  card: {
    par: 'The right model',
    about: (amount: string) => `about ${amount}`,
  },

  /* --- The public page (components/profile/PublicProfile.tsx) ------------ */
  person: {
    label: 'What their agents cost',
    line: (ratio: string, n: number, book: string) =>
      `Median ${ratio}× par across ${num(n, 'en')} checkpoints (price book ${book})`,
    what: 'Par is what the cheapest AI model that passed every one of EngineerOS’s lab runs paid to pass the same checkpoint, measured by EngineerOS at a fixed price book. Under 1× means this learner’s agents reached a passing result for less, over 1× for more. Every metered pass counts, not only the best, and the costs were counted at EngineerOS’s own gateway rather than reported by the learner.',
  },

  /* --- The study, /study ------------------------------------------------- */
  study: {
    docTitle: 'The agent cost study · EngineerOS',
    kicker: 'Study',
    title: 'The agent cost study',
    lede: 'We want to find out whether learning on EngineerOS changes what your AI agent spends to reach a correct result. We will publish what the study finds, whatever it finds.',
    stepsTitle: 'If you join',
    consentIntro: 'The EngineerOS efficiency study. We are measuring whether our lessons make AI agents reach a correct result for less money.',
    steps: (weeks: number, first: number, last: number, credit: string) => [
      'You take a benchmark of four tasks now, using an AI agent through your EngineerOS AI key. We pay for the model usage on benchmark tasks; it does not come out of your credits.',
      `You then work through levels ${num(first, 'en')} to ${num(last, 'en')} as usual, for up to ${num(weeks, 'en')} weeks. During that time some participants see their cost against par and a suggested technique after each pass, and some see it only after the study. Which group you are in is decided at random and is not shown to you. Everybody gets the full feedback by the end.`,
      'You then take a second benchmark of four similar tasks.',
      `When you finish both benchmarks you receive ${credit} of AI credit. Like other credits it has no cash value.`,
    ],
    consentTitle: 'What we record, and what we never do',
    consent: [
      'For each model call: the model, the token counts, the cost and the time, linked to the task or level you were doing; and each task’s result (passed or not) and how long it took.',
      'We never record what you or your agent wrote: no prompts, no answers, no code.',
      'Results are published only in aggregate, never with your name, your account or anything that identifies you.',
      'You can withdraw at any time, without giving a reason, from this page. Your study results are then deleted and left out of the analysis; you keep everything else, including any credit already received.',
    ],
    agree: 'I am 18 or older, I have read this, and I agree to take part.',
    join: 'Join the study',
    joining: 'Joining…',
    signIn: 'Sign in to join the study.',
    signInButton: 'Sign in',
    loading: 'Loading…',
    unreachable: 'The study could not be reached just now. Try again in a moment.',
    failed: 'That did not work. Try again.',
    refusals: {
      closed: 'The study is not open for new participants right now.',
      'no-ai-key': 'To join, first make your EngineerOS AI key: only calls through it are measured.',
      'no-course': 'The study needs the course for the current period, because some of its levels are paid.',
      'no-ai-credits': 'AI credits are not switched on here, so the study cannot measure anything yet.',
      enrolled: 'You have already joined the study. Somebody who has left cannot join again.',
      consent: 'The consent text has changed. Reload the page, read it again and agree to the new version.',
    } as Record<StudyRefusal, string>,

    benchTitle: (n: number) => `Benchmark ${num(n, 'en')}`,
    benchIntro: 'Four tasks, each done by your agent, in any order. Each task gets one go: the first attempt that closes is the result.',
    kinds: ['Fix a bug from a report', 'Add a feature, with tests', 'Refactor without changing behaviour', 'Debug a broken service'] as BenchKinds,
    taskId: 'Task id',
    /** Opens the task in the play screen, as the agent's open_level does. */
    start: 'Start',
    resume: 'Continue',
    states: { todo: 'Not started', open: 'In progress', passed: 'Passed', failed: 'Not passed', capped: 'Stopped at the limit' } as Record<BenchTaskState, string>,
    howTitle: 'How to start a task with your agent',
    how: (connect: string, check: string) => [
      'Connect your agent to EngineerOS once, with an agent key from the agent page.',
      `Open the course in a tab and press “${connect}”.`,
      'Ask your agent to call open_level with the task id shown beside the task.',
      `Your agent works until ${check} passes. A task solved by hand does not count for the benchmark.`,
    ],
    limit: (minutes: number) =>
      `A task stops counting after ${num(minutes, 'en')} minutes, or when it reaches its cost limit. Either one counts as not passed.`,
    funded: 'Benchmark model calls are paid by EngineerOS, not from your credits.',
    openCourse: 'Open the course',
    setUp: 'Set up your agent',

    learningTitle: 'Benchmark 1 is done',
    learning: (first: number, last: number, date: string) =>
      `Take the course as usual. Benchmark 2 opens when you finish levels ${num(first, 'en')} to ${num(last, 'en')}, or on ${date}, whichever comes first.`,
    learningNoDate: (first: number, last: number, days: number) =>
      `Take the course as usual. Benchmark 2 opens when you finish levels ${num(first, 'en')} to ${num(last, 'en')}, or ${num(days, 'en')} days after Benchmark 1, whichever comes first.`,
    doneTitle: 'You finished the study',
    done: (credit: string) => `Thank you. ${credit} of AI credit is in your balance.`,
    doneNoCredit: 'Thank you. Your AI credit is on its way to your balance.',
    results: 'The results will be published, in aggregate, when the study closes.',
    withdrawnTitle: 'You have left the study',
    withdrawn: 'Your study results were deleted and are left out of the analysis. You keep everything else, including any credit already received.',
    leave: 'Leave the study',
    leaveConfirm: 'Leave the study? Your study results are deleted and left out of the analysis, and you cannot join again. You keep everything else, including any credit already received.',
    leaveYes: 'Yes, leave',
    stay: 'Stay',
    date: (at: number) => calendarDate(new Date(at), 'en'),
  },
};

/* ── Arabic ───────────────────────────────────────────────────────────────── */

/** `مكالمة واحدة`, `مكالمتان`, `3 مكالمات`, `11 مكالمة` (docs/i18n.md §4). */
const callsAr = (n: number) =>
  n === 1 ? 'استدعاء واحد للنموذج'
    : n === 2 ? 'استدعاءان للنموذج'
    : n % 100 >= 3 && n % 100 <= 10 ? `${num(n, 'ar')} استدعاءات للنموذج`
    : `${num(n, 'ar')} استدعاءً للنموذج`;

const checkpointsAr = (n: number) =>
  n === 1 ? 'محطة واحدة' : n === 2 ? 'محطتين' : n % 100 >= 3 && n % 100 <= 10 ? `${num(n, 'ar')} محطات` : `${num(n, 'ar')} محطة`;

const daysAr = (n: number) =>
  n === 1 ? 'يوم واحد' : n === 2 ? 'يومين' : n % 100 >= 3 && n % 100 <= 10 ? `${num(n, 'ar')} أيام` : `${num(n, 'ar')} يومًا`;

const weeksAr = (n: number) =>
  n === 1 ? 'أسبوع واحد' : n === 2 ? 'أسبوعين' : n % 100 >= 3 && n % 100 <= 10 ? `${num(n, 'ar')} أسابيع` : `${num(n, 'ar')} أسبوعًا`;

const minutesAr = (n: number) =>
  n === 1 ? 'دقيقة واحدة' : n === 2 ? 'دقيقتين' : n % 100 >= 3 && n % 100 <= 10 ? `${num(n, 'ar')} دقائق` : `${num(n, 'ar')} دقيقة`;

export const ar: typeof en = {
  under: (cent: string) => `أقل من ${cent}`,
  // The ratio and its sign are one machine run, kept left to right.
  ratio: (x: string) => `${ltr(`${x}×`)} المرجع`,
  calls: callsAr,

  clear: {
    heading: 'كم كلّف',
    yours: 'وكيلك',
    par: 'النموذج المناسب',
    noPar: 'لم نقِس المرجع لهذا بعد.',
    reported: 'محسوبة مما أبلغت عنه أداة وكيلك.',
    costliest: 'أغلى الاستدعاءات',
    tokens: (input: number, output: number) => `${num(input, 'ar')} وحدة دخلت، و${num(output, 'ar')} خرجت`,
    tryThis: 'شيء واحد تجرّبه',
    how: 'كيف',
    tryModel: (model: string) => `جرّب هنا ${ltr(model)}، فقد اجتاز كل تجاربنا عليه في المختبر.`,
  },

  techniques: {
    'scope-context': {
      name: 'حدِّد السياق',
      line: 'سمِّ الملفات التي يحتاجها الوكيل، بدل أن تتركه يقرأ المستودع كله.',
      body: 'كل ملف يقرؤه الوكيل يدخل في سياقه، وكل دور بعده يقرأ ذلك السياق من جديد. أخبره بالملفات المهمة وبما يتركه. النطاق القصير المسمّى أقل كلفة في كل دور يليه.',
    },
    'right-size-model': {
      name: 'اختر أصغر نموذج يؤدّي المهمة',
      line: 'ابدأ بأصغر نموذج، ولا تنتقل إلى أكبر منه إلا إذا أخفق.',
      body: 'وجدت مقارنات Claude و Codex في مختبرنا أن نموذجًا أصغر يستطيع بلوغ النتيجة الصحيحة نفسها بتكلفة أقل. ابدأ بأصغر نموذج مناسب لمهمتك وتحقق من عمله. إن أخفق أو دار في حلقة، انتقل إلى نموذج أكبر لتلك الخطوة ثم عُد إلى الأصغر. قِس الرحلة كاملة حتى النتيجة الصحيحة، بما فيها المحاولات الفاشلة؛ فقد تحتاج مهمتك إلى نموذج مختلف.',
    },
    restart: {
      name: 'ابدأ من جديد بدل الجدال',
      line: 'بعد منعطف خاطئ، امسح الجلسة وابدأ من ملخّص قصير.',
      body: 'يكبر السياق مع كل دور، وكل دور يقرؤه كله من جديد. أن تصحّح الوكيل خمس مرات يعني أن تدفع ثمن الخطأ خمس مرات. اكتب في سطرين ما تعرفه الآن، وافتح جلسة جديدة، وأعطها السطرين.',
    },
    'exact-bug-report': {
      name: 'صِف الخلل بدقة',
      line: 'أعطِ العَرَض والخطوات وما كنت تتوقعه.',
      body: '«إنه معطّل» يرسل الوكيل يبحث في المشروع كله. الخطأ بنصّه، والخطوات التي تسبّبه، وما كان يجب أن يحدث بدلًا منه، تأخذه مباشرة إلى السبب.',
    },
    'stable-prefix': {
      name: 'أبقِ بداية المحادثة ثابتة',
      line: 'أبقِ أول المحادثة كما هو، ليعمل التخزين المؤقت للتعليمات.',
      body: 'يتقاضى المزوّدون أقل مقابل دخل سبق أن رأوه في أول المحادثة. التعليمات التي تتغيّر في كل دور تكسر هذا التخزين. ضع ما يبقى كما هو أولًا، ولا تلمسه.',
    },
  },

  page: {
    docTitle: 'كم يكلّف الوكيل · EngineerOS',
    kicker: 'الكفاءة',
    title: 'كم يكلّف الوكيل، والعادات التي تغيّر ذلك',
    lede: 'بعد أن يجتاز وكيلك مستوى، يعرض المستوى كم كلّف مقارنة بالمرجع، ويشير إلى عادة واحدة من العادات أدناه تدلّ عليها استدعاءات وكيلك. أوّلها هو ما قاسه مختبرنا: أصغر نموذج ما زال يؤدّي المهمة. تبقى كل عادة في هذه القائمة ما دامت تجارب مختبرنا تؤيّدها.',
    parTitle: 'ما معنى المرجع',
    parBody: (book: string) =>
      `المرجع هو ما دفعه أرخص نموذج اجتاز كل تجاربنا في المختبر ليجتاز المستوى أو المحطة أو المهمة نفسها، ونحن من يقيسه. تُسعَّر كل كلفة بجدول أسعار ثابت، ${ltr(book)}، فلا يحرّك المقارنةَ مزوّدٌ غيّر أسعاره. ${ltr('1.0×')} هو المرجع.`,
    hand: 'لا يُقارَن بالمرجع إلا الاجتياز. العمل باليد مسموح دائمًا، وليس له رقم كلفة.',
    private: 'تُحسب الكلفة عند بوابتنا نحن، من النموذج وعدد الوحدات والسعر. ولا يُخزَّن نصّ تعليماتك ولا نصّ الأجوبة أبدًا.',
    listTitle: 'العادات',
    back: 'العودة إلى المسار',
  },

  costs: {
    vsPar: 'مقارنة بالمرجع',
  },
  card: {
    par: 'النموذج المناسب',
    about: (amount: string) => `نحو ${amount}`,
  },

  person: {
    label: 'كم يكلّف وكلاؤه',
    line: (ratio: string, n: number, book: string) =>
      `الوسيط ${ltr(`${ratio}×`)} المرجع على ${checkpointsAr(n)} (جدول الأسعار ${ltr(book)})`,
    what: 'المرجع هو ما دفعه أرخص نموذج ذكاء اصطناعي اجتاز كل تجارب EngineerOS في المختبر ليجتاز المحطة نفسها، وتقيسه EngineerOS بجدول أسعار ثابت. أقل من 1× يعني أن وكلاء هذا المتعلّم بلغوا نتيجة ناجحة بكلفة أقل، وأكثر من 1× بكلفة أكبر. يُحسب كل اجتياز مقيس، لا الأفضل وحده، وحُسبت الكلفة عند بوابة EngineerOS نفسها، لا بما أبلغ عنه المتعلّم.',
  },

  study: {
    docTitle: 'دراسة كلفة الوكيل · EngineerOS',
    kicker: 'دراسة',
    title: 'دراسة كلفة الوكيل',
    lede: 'نريد أن نعرف هل يغيّر التعلّم على EngineerOS ما ينفقه وكيل الذكاء الاصطناعي الخاص بك ليصل إلى نتيجة صحيحة. سننشر ما تجده الدراسة، أيًّا كان.',
    stepsTitle: 'إن انضممت',
    consentIntro: 'دراسة الكفاءة في EngineerOS. نقيس هل تجعل دروسنا وكلاء الذكاء الاصطناعي يصلون إلى نتيجة صحيحة بمال أقل.',
    steps: (weeks: number, first: number, last: number, credit: string) => [
      'تأخذ الآن اختبارًا معياريًا من أربع مهام، مستخدمًا وكيل ذكاء اصطناعي عبر مفتاح EngineerOS للذكاء الاصطناعي الخاص بك. نحن ندفع ثمن استخدام النموذج في مهام الاختبار المعياري، ولا يُخصم من رصيدك.',
      `ثم تعمل على المستويات من ${num(first, 'ar')} إلى ${num(last, 'ar')} كالمعتاد، لمدة أقصاها ${weeksAr(weeks)}. خلال ذلك يرى بعض المشاركين كلفتهم مقارنة بالمرجع وأسلوبًا مقترحًا بعد كل اجتياز، ولا يراها بعضهم إلا بعد الدراسة. تُحدَّد مجموعتك عشوائيًا ولا تُعرض عليك. ويحصل الجميع على الملاحظات كاملة في النهاية.`,
      'ثم تأخذ اختبارًا معياريًا ثانيًا من أربع مهام مشابهة.',
      `حين تُنهي الاختبارين المعياريين تحصل على ${credit} رصيدًا للذكاء الاصطناعي. ومثل أي رصيد آخر، ليست له قيمة نقدية.`,
    ],
    consentTitle: 'ما نسجّله، وما لا نفعله أبدًا',
    consent: [
      'لكل استدعاء للنموذج: النموذج، وعدد الوحدات، والكلفة، والوقت، مربوطة بالمهمة أو المستوى الذي كنت تعمل عليه؛ ونتيجة كل مهمة (اجتازت أم لا) وكم استغرقت.',
      'لا نسجّل أبدًا ما كتبته أنت أو وكيلك: لا تعليمات، ولا أجوبة، ولا كود.',
      'تُنشر النتائج مجمَّعة فقط، ولا تُنشر أبدًا مع اسمك أو حسابك أو أي شيء يعرّف بك.',
      'يمكنك الانسحاب في أي وقت، دون ذكر سبب، من هذه الصفحة. تُحذف عندها نتائجك في الدراسة وتُستبعد من التحليل، وتحتفظ بكل ما عدا ذلك، ومنه أي رصيد حصلت عليه.',
    ],
    agree: 'عمري 18 عامًا أو أكثر، وقرأت هذا، وأوافق على المشاركة.',
    join: 'انضم إلى الدراسة',
    joining: 'جارٍ الانضمام…',
    signIn: 'سجّل الدخول لتنضم إلى الدراسة.',
    signInButton: 'تسجيل الدخول',
    loading: 'جارٍ التحميل…',
    unreachable: 'تعذّر الوصول إلى الدراسة الآن. حاول بعد قليل.',
    failed: 'لم ينجح ذلك. حاول مجددًا.',
    refusals: {
      closed: 'الدراسة غير مفتوحة لمشاركين جدد الآن.',
      'no-ai-key': 'لتنضم، أنشئ أولًا مفتاح EngineerOS للذكاء الاصطناعي: لا يُقاس إلا ما يمرّ عبره.',
      'no-course': 'تحتاج الدراسة إلى اشتراكك في المسار للفترة الحالية، لأن بعض مستوياتها مدفوعة.',
      'no-ai-credits': 'رصيد الذكاء الاصطناعي غير مفعَّل هنا، فلا تستطيع الدراسة قياس شيء بعد.',
      enrolled: 'انضممت إلى الدراسة من قبل. ومن غادرها لا يستطيع الانضمام مجددًا.',
      consent: 'تغيّر نص الموافقة. أعد تحميل الصفحة واقرأه مجددًا ووافق على النسخة الجديدة.',
    },

    benchTitle: (n: number) => (n === 1 ? 'الاختبار المعياري الأول' : n === 2 ? 'الاختبار المعياري الثاني' : `الاختبار المعياري ${num(n, 'ar')}`),
    benchIntro: 'أربع مهام، ينجز وكيلك كل واحدة منها، بأي ترتيب. لكل مهمة محاولة واحدة: أول محاولة تُغلق هي النتيجة.',
    kinds: ['أصلح خللًا من بلاغ', 'أضف ميزة مع اختباراتها', 'أعد الهيكلة دون تغيير السلوك', 'شخّص خدمة معطّلة'],
    taskId: 'معرّف المهمة',
    start: 'ابدأ',
    resume: 'تابع',
    states: { todo: 'لم تبدأ', open: 'قيد العمل', passed: 'اجتازها', failed: 'لم يجتزها', capped: 'توقفت عند الحد' },
    howTitle: 'كيف تبدأ مهمة مع وكيلك',
    how: (connect: string, check: string) => [
      'اربط وكيلك بـ EngineerOS مرة واحدة، بمفتاح وكيل.',
      `افتح المسار في تبويب واضغط «${connect}».`,
      `اطلب من وكيلك أن يستدعي ${ltr('open_level')} بمعرّف المهمة المكتوب بجانبها.`,
      `يعمل وكيلك حتى ينجح «${check}». المهمة التي تُحلّ باليد لا تُحسب في الاختبار المعياري.`,
    ],
    limit: (minutes: number) =>
      `تتوقف المهمة عن الاحتساب بعد ${minutesAr(minutes)}، أو حين تبلغ حدّ كلفتها. وأيّ منهما يُحسب عدم اجتياز.`,
    funded: 'تدفع EngineerOS ثمن استدعاءات النموذج في الاختبار المعياري، لا من رصيدك.',
    openCourse: 'افتح المسار',
    setUp: 'اضبط وكيلك',

    learningTitle: 'انتهى الاختبار المعياري الأول',
    learning: (first: number, last: number, date: string) =>
      `تابع المسار كالمعتاد. يُفتح الاختبار المعياري الثاني حين تُنهي المستويات من ${num(first, 'ar')} إلى ${num(last, 'ar')}، أو في ${date}، أيّهما أسبق.`,
    learningNoDate: (first: number, last: number, days: number) =>
      `تابع المسار كالمعتاد. يُفتح الاختبار المعياري الثاني حين تُنهي المستويات من ${num(first, 'ar')} إلى ${num(last, 'ar')}، أو بعد ${daysAr(days)} من الاختبار الأول، أيّهما أسبق.`,
    doneTitle: 'أنهيت الدراسة',
    done: (credit: string) => `شكرًا لك. أُضيف ${credit} رصيدًا للذكاء الاصطناعي إلى رصيدك.`,
    doneNoCredit: 'شكرًا لك. رصيدك للذكاء الاصطناعي في الطريق إليك.',
    results: 'ستُنشر النتائج مجمَّعة حين تُغلق الدراسة.',
    withdrawnTitle: 'غادرت الدراسة',
    withdrawn: 'حُذفت نتائجك في الدراسة واستُبعدت من التحليل. تحتفظ بكل ما عدا ذلك، ومنه أي رصيد حصلت عليه.',
    leave: 'غادر الدراسة',
    leaveConfirm: 'تغادر الدراسة؟ تُحذف نتائجك فيها وتُستبعد من التحليل، ولا يمكنك الانضمام مجددًا. تحتفظ بكل ما عدا ذلك، ومنه أي رصيد حصلت عليه.',
    leaveYes: 'نعم، غادر',
    stay: 'ابقَ',
    date: (at: number) => calendarDate(new Date(at), 'ar'),
  },
};

/* ── German ───────────────────────────────────────────────────────────────── */

export const de: typeof en = {
  under: (cent: string) => `unter ${cent}`,
  ratio: (x: string) => `${x}× Par`,
  calls: (n: number) => (n === 1 ? '1 Modellaufruf' : `${num(n, 'de')} Modellaufrufe`),

  clear: {
    heading: 'Was es gekostet hat',
    yours: 'Dein Agent',
    par: 'Das passende Modell',
    noPar: 'Für diese Aufgabe ist Par noch nicht gemessen.',
    reported: 'Gezählt aus dem, was das Werkzeug deines Agenten gemeldet hat.',
    costliest: 'Die teuersten Aufrufe',
    tokens: (input: number, output: number) => `${num(input, 'de')} Tokens rein, ${num(output, 'de')} raus`,
    tryThis: 'Eine Sache zum Ausprobieren',
    how: 'Wie',
    tryModel: (model: string) => `Probier hier ${ltr(model)}: Es hat jeden unserer Laborläufe dafür bestanden.`,
  },

  techniques: {
    'scope-context': {
      name: 'Den Kontext eingrenzen',
      line: 'Nenne die Dateien, die der Agent braucht, statt ihn das ganze Repository lesen zu lassen.',
      body: 'Jede Datei, die der Agent liest, landet in seinem Kontext, und jeder spätere Schritt liest diesen Kontext erneut. Sag ihm, welche Dateien zählen und was er liegen lassen soll. Ein kurzer, benannter Ausschnitt kostet in jedem folgenden Schritt weniger.',
    },
    'right-size-model': {
      name: 'Das kleinste Modell wählen, das es schafft',
      line: 'Fang mit dem kleinsten Modell an und wechsle nur nach oben, wenn es scheitert.',
      body: 'Unsere Laborvergleiche mit Claude und Codex zeigten, dass ein kleineres Modell dasselbe korrekte Ergebnis günstiger erreichen kann. Beginne mit dem kleinsten geeigneten Modell und prüfe seine Arbeit. Wenn es scheitert oder sich im Kreis dreht, wechsle für diesen Schritt zu einem größeren Modell und danach zurück. Miss den gesamten Weg zum korrekten Ergebnis einschließlich gescheiterter Versuche; deine Aufgabe kann ein anderes Modell erfordern.',
    },
    restart: {
      name: 'Neu anfangen statt streiten',
      line: 'Nach einem Irrweg die Sitzung leeren und mit einer kurzen Zusammenfassung neu beginnen.',
      body: 'Der Kontext wächst mit jedem Schritt, und jeder Schritt liest ihn ganz. Einen Agenten fünfmal zu korrigieren heißt, den Fehler fünfmal zu bezahlen. Schreib in zwei Zeilen auf, was du jetzt weißt, öffne eine neue Sitzung und gib ihr diese Zeilen.',
    },
    'exact-bug-report': {
      name: 'Fehler genau melden',
      line: 'Nenne das Symptom, die Schritte und was du erwartet hast.',
      body: '„Es ist kaputt“ schickt den Agenten durch das ganze Projekt. Der genaue Fehler, die Schritte dorthin und was stattdessen hätte passieren sollen führen ihn direkt zur Ursache.',
    },
    'stable-prefix': {
      name: 'Den Anfang stabil halten',
      line: 'Halte den Anfang des Gesprächs gleich, damit Prompt-Caching greift.',
      body: 'Anbieter verlangen weniger für Eingaben, die sie am Anfang eines Gesprächs schon gesehen haben. Anweisungen, die sich in jedem Schritt ändern, brechen diesen Cache. Stell das Gleichbleibende an den Anfang und lass es dort.',
    },
  },

  page: {
    docTitle: 'Was ein Agent kostet · EngineerOS',
    kicker: 'Effizienz',
    title: 'Was ein Agent kostet, und die Gewohnheiten, die das ändern',
    lede: 'Nachdem dein Agent ein Level bestanden hat, zeigt das Level, was es im Vergleich zu Par gekostet hat, und verweist auf die eine Gewohnheit unten, auf die die Aufrufe deines Agenten hindeuten. Die erste hat unser Labor gemessen: das kleinste Modell, das die Aufgabe noch schafft. Jede Gewohnheit bleibt nur so lange auf dieser Liste, wie unsere eigenen Laborläufe sie stützen.',
    parTitle: 'Was Par bedeutet',
    parBody: (book: string) =>
      `Par ist, was das günstigste Modell, das jeden unserer Laborläufe bestanden hat, bezahlt hat, um dasselbe Level, denselben Checkpoint oder dieselbe Aufgabe zu bestehen, von uns gemessen. Jede Kostenangabe wird mit einem festen Preisbuch berechnet, ${book}, damit eine Preisänderung eines Anbieters den Vergleich nie verschiebt. 1,0× ist Par.`,
    hand: 'Nur ein Bestehen wird mit Par verglichen. Arbeit von Hand ist immer erlaubt und hat keine Kostenangabe.',
    private: 'Die Kosten werden an unserem eigenen Gateway gezählt, aus Modell, Tokenzahlen und Preis. Der Text deiner Prompts und der Antworten wird nie gespeichert.',
    listTitle: 'Die Gewohnheiten',
    back: 'Zurück zum Kurs',
  },

  costs: {
    vsPar: 'Im Vergleich zu Par',
  },
  card: {
    par: 'Das passende Modell',
    about: (amount: string) => `etwa ${amount}`,
  },

  person: {
    label: 'Was die Agenten kosten',
    line: (ratio: string, n: number, book: string) =>
      `Median ${ratio}× Par über ${num(n, 'de')} Checkpoints (Preisbuch ${book})`,
    what: 'Par ist, was das günstigste KI-Modell, das jeden Laborlauf von EngineerOS bestanden hat, bezahlt hat, um denselben Checkpoint zu bestehen, gemessen von EngineerOS mit einem festen Preisbuch. Unter 1× heißt, die Agenten dieser Person haben ein bestandenes Ergebnis günstiger erreicht, über 1× teurer. Jedes gemessene Bestehen zählt, nicht nur das beste, und die Kosten wurden am eigenen Gateway von EngineerOS gezählt, nicht von der Person gemeldet.',
  },

  study: {
    docTitle: 'Die Studie zu Agentenkosten · EngineerOS',
    kicker: 'Studie',
    title: 'Die Studie zu Agentenkosten',
    lede: 'Wir wollen herausfinden, ob das Lernen auf EngineerOS verändert, was dein KI-Agent ausgibt, um ein korrektes Ergebnis zu erreichen. Wir veröffentlichen, was die Studie findet, was immer es ist.',
    stepsTitle: 'Wenn du teilnimmst',
    consentIntro: 'Die Effizienzstudie von EngineerOS. Wir messen, ob unsere Lektionen KI-Agenten mit weniger Geld zu einem korrekten Ergebnis bringen.',
    steps: (weeks: number, first: number, last: number, credit: string) => [
      'Du machst jetzt einen Benchmark mit vier Aufgaben, mit einem KI-Agenten über deinen EngineerOS-KI-Schlüssel. Die Modellnutzung bei Benchmark-Aufgaben bezahlen wir; sie geht nicht von deinem Guthaben ab.',
      `Danach bearbeitest du die Level ${num(first, 'de')} bis ${num(last, 'de')} wie gewohnt, höchstens ${num(weeks, 'de')} Wochen lang. In dieser Zeit sehen manche Teilnehmenden nach jedem Bestehen ihre Kosten im Vergleich zu Par und eine vorgeschlagene Technik, andere erst nach der Studie. In welcher Gruppe du bist, wird zufällig bestimmt und dir nicht angezeigt. Am Ende bekommen alle die vollständige Rückmeldung.`,
      'Danach machst du einen zweiten Benchmark mit vier ähnlichen Aufgaben.',
      `Wenn du beide Benchmarks abschließt, bekommst du ${credit} KI-Guthaben. Wie anderes Guthaben hat es keinen Geldwert.`,
    ],
    consentTitle: 'Was wir aufzeichnen, und was wir nie tun',
    consent: [
      'Für jeden Modellaufruf: das Modell, die Tokenzahlen, die Kosten und die Zeit, verknüpft mit der Aufgabe oder dem Level, an dem du gearbeitet hast; und für jede Aufgabe das Ergebnis (bestanden oder nicht) und wie lange sie gedauert hat.',
      'Wir zeichnen nie auf, was du oder dein Agent geschrieben habt: keine Prompts, keine Antworten, keinen Code.',
      'Ergebnisse werden nur zusammengefasst veröffentlicht, nie mit deinem Namen, deinem Konto oder irgendetwas, das dich erkennbar macht.',
      'Du kannst jederzeit ohne Angabe von Gründen auf dieser Seite aussteigen. Deine Studienergebnisse werden dann gelöscht und nicht ausgewertet; alles andere behältst du, auch bereits erhaltenes Guthaben.',
    ],
    agree: 'Ich bin mindestens 18 Jahre alt, habe das gelesen und stimme der Teilnahme zu.',
    join: 'An der Studie teilnehmen',
    joining: 'Wird angemeldet…',
    signIn: 'Melde dich an, um an der Studie teilzunehmen.',
    signInButton: 'Anmelden',
    loading: 'Wird geladen…',
    unreachable: 'Die Studie ist gerade nicht erreichbar. Versuch es gleich noch einmal.',
    failed: 'Das hat nicht geklappt. Versuch es noch einmal.',
    refusals: {
      closed: 'Die Studie nimmt gerade keine neuen Teilnehmenden auf.',
      'no-ai-key': 'Erstelle zuerst deinen EngineerOS-KI-Schlüssel: Gemessen wird nur, was über ihn läuft.',
      'no-course': 'Für die Studie brauchst du den Kurs für den aktuellen Zeitraum, weil einige ihrer Level kostenpflichtig sind.',
      'no-ai-credits': 'KI-Guthaben ist hier nicht eingeschaltet, deshalb kann die Studie noch nichts messen.',
      enrolled: 'Du nimmst bereits an der Studie teil. Wer ausgestiegen ist, kann nicht erneut teilnehmen.',
      consent: 'Der Einwilligungstext hat sich geändert. Lade die Seite neu, lies ihn noch einmal und stimme der neuen Fassung zu.',
    },

    benchTitle: (n: number) => (n === 1 ? 'Erster Benchmark' : n === 2 ? 'Zweiter Benchmark' : `Benchmark Nr. ${num(n, 'de')}`),
    benchIntro: 'Vier Aufgaben, jede von deinem Agenten erledigt, in beliebiger Reihenfolge. Jede Aufgabe hat einen Versuch: Der erste Versuch, der endet, ist das Ergebnis.',
    kinds: ['Einen Fehler aus einer Meldung beheben', 'Eine Funktion mit Tests ergänzen', 'Umbauen, ohne das Verhalten zu ändern', 'Einen kaputten Dienst untersuchen'],
    taskId: 'Aufgaben-ID',
    start: 'Starten',
    resume: 'Weiter',
    states: { todo: 'Nicht begonnen', open: 'In Arbeit', passed: 'Bestanden', failed: 'Nicht bestanden', capped: 'Am Limit gestoppt' },
    howTitle: 'So startest du eine Aufgabe mit deinem Agenten',
    how: (connect: string, check: string) => [
      'Verbinde deinen Agenten einmal mit EngineerOS, mit einem Agent-Schlüssel.',
      `Öffne den Kurs in einem Tab und drücke „${connect}“.`,
      'Bitte deinen Agenten, open_level mit der Aufgaben-ID neben der Aufgabe aufzurufen.',
      `Dein Agent arbeitet, bis „${check}“ besteht. Eine von Hand gelöste Aufgabe zählt für den Benchmark nicht.`,
    ],
    limit: (minutes: number) =>
      `Eine Aufgabe zählt nach ${num(minutes, 'de')} Minuten nicht mehr, oder sobald sie ihr Kostenlimit erreicht. Beides zählt als nicht bestanden.`,
    funded: 'Die Modellaufrufe im Benchmark bezahlt EngineerOS, nicht dein Guthaben.',
    openCourse: 'Kurs öffnen',
    setUp: 'Agent einrichten',

    learningTitle: 'Benchmark 1 ist erledigt',
    learning: (first: number, last: number, date: string) =>
      `Mach mit dem Kurs wie gewohnt weiter. Benchmark 2 öffnet, wenn du die Level ${num(first, 'de')} bis ${num(last, 'de')} abgeschlossen hast, oder am ${date}, je nachdem, was zuerst eintritt.`,
    learningNoDate: (first: number, last: number, days: number) =>
      `Mach mit dem Kurs wie gewohnt weiter. Benchmark 2 öffnet, wenn du die Level ${num(first, 'de')} bis ${num(last, 'de')} abgeschlossen hast, oder ${num(days, 'de')} Tage nach Benchmark 1, je nachdem, was zuerst eintritt.`,
    doneTitle: 'Du hast die Studie abgeschlossen',
    done: (credit: string) => `Danke. ${credit} KI-Guthaben ist in deinem Guthaben.`,
    doneNoCredit: 'Danke. Dein KI-Guthaben ist unterwegs.',
    results: 'Die Ergebnisse werden zusammengefasst veröffentlicht, wenn die Studie endet.',
    withdrawnTitle: 'Du hast die Studie verlassen',
    withdrawn: 'Deine Studienergebnisse wurden gelöscht und werden nicht ausgewertet. Alles andere behältst du, auch bereits erhaltenes Guthaben.',
    leave: 'Studie verlassen',
    leaveConfirm: 'Die Studie verlassen? Deine Studienergebnisse werden gelöscht und nicht ausgewertet, und du kannst nicht erneut teilnehmen. Alles andere behältst du, auch bereits erhaltenes Guthaben.',
    leaveYes: 'Ja, verlassen',
    stay: 'Bleiben',
    date: (at: number) => calendarDate(new Date(at), 'de'),
  },
};

/* ── Japanese ─────────────────────────────────────────────────────────────── */

export const ja: typeof en = {
  under: (cent: string) => `${cent}未満`,
  ratio: (x: string) => `パーの${x}倍`,
  calls: (n: number) => `モデル呼び出し${num(n, 'ja')}回`,

  clear: {
    heading: 'かかったコスト',
    yours: 'あなたのエージェント',
    par: '適切なモデル',
    noPar: 'この課題のパーはまだ測定していません。',
    reported: 'エージェントのツールが報告した内容から計算しています。',
    costliest: '最もコストのかかった呼び出し',
    tokens: (input: number, output: number) => `入力${num(input, 'ja')}トークン、出力${num(output, 'ja')}トークン`,
    tryThis: '試してほしいこと',
    how: '方法',
    tryModel: (model: string) => `この課題では ${ltr(model)} を試してください。私たちのラボでは毎回合格しました。`,
  },

  techniques: {
    'scope-context': {
      name: 'コンテキストを絞る',
      line: 'リポジトリ全体を読ませるのではなく、エージェントに必要なファイルを指定します。',
      body: 'エージェントが読んだファイルはすべてコンテキストに入り、その後のターンは毎回そのコンテキストを読み直します。どのファイルが重要で、何に触れないかを伝えましょう。短く名前のついた範囲は、その後のすべてのターンでコストが小さくなります。',
    },
    'right-size-model': {
      name: '仕事をこなせる最小のモデルを選ぶ',
      line: '最小のモデルから始め、失敗したときだけ大きくする。',
      body: 'ClaudeとCodexの実験では、小さいモデルでも同じ正しい結果により低い費用で到達できました。課題に適した最小のモデルから始め、結果を確認します。失敗したり同じところを回ったりしたら、その工程だけ大きいモデルに切り替えてから戻します。失敗した試行も含めて正しい結果までの全費用を測りましょう。自分の課題には別のモデルが必要かもしれません。',
    },
    restart: {
      name: '言い争うより、やり直す',
      line: '間違った方向に進んだら、セッションを消して短い要約から始め直します。',
      body: 'コンテキストはターンごとに大きくなり、毎ターンその全体が読み直されます。エージェントを5回直せば、その間違いに5回お金を払うことになります。今わかっていることを2行にまとめ、新しいセッションを開いて、その2行を渡しましょう。',
    },
    'exact-bug-report': {
      name: 'バグを正確に伝える',
      line: '症状、再現手順、期待していた結果を伝えます。',
      body: '「壊れています」だけでは、エージェントはプロジェクト全体を探し回ります。正確なエラー、それを起こす手順、本来どうなるべきだったかを伝えれば、原因にまっすぐ向かえます。',
    },
    'stable-prefix': {
      name: '冒頭を変えない',
      line: 'プロンプトキャッシュが効くよう、会話の冒頭を同じに保ちます。',
      body: 'プロバイダーは、会話の冒頭ですでに見た入力を安く請求します。ターンごとに変わる指示は、そのキャッシュを壊します。変わらない部分を最初に置き、そのままにしておきましょう。',
    },
  },

  page: {
    docTitle: 'エージェントのコスト · EngineerOS',
    kicker: '効率',
    title: 'エージェントのコストと、それを変える習慣',
    lede: 'エージェントでレベルに合格すると、レベルはパーと比べたコストを表示し、エージェントの呼び出しから見えてくる下の習慣を1つ示します。最初の習慣は私たちのラボで測定したもの、つまり仕事をこなせる最小のモデルを選ぶことです。各習慣は、私たち自身のラボでの実行結果が裏づける間だけ、このリストに残ります。',
    parTitle: 'パーとは',
    parBody: (book: string) =>
      `パーとは、私たちのラボのすべての実行で合格した最も安いモデルが同じレベル、チェックポイント、タスクに合格するのに支払った額で、私たちが測定します。すべてのコストは固定の価格表 ${book} で計算するため、プロバイダーが価格を変えても比較は動きません。1.0倍がパーです。`,
    hand: 'パーと比べるのは合格だけです。手作業は常に認められ、コストの数字はつきません。',
    private: 'コストは私たち自身のゲートウェイで、モデル、トークン数、価格から数えます。プロンプトや回答の本文は一切保存しません。',
    listTitle: '習慣',
    back: 'コースに戻る',
  },

  costs: {
    vsPar: 'パーとの比較',
  },
  card: {
    par: '適切なモデル',
    about: (amount: string) => `約${amount}`,
  },

  person: {
    label: 'エージェントのコスト',
    line: (ratio: string, n: number, book: string) =>
      `${num(n, 'ja')}件のチェックポイントで中央値はパーの${ratio}倍(価格表 ${book})`,
    what: 'パーとは、EngineerOSのラボのすべての実行で合格した最も安いAIモデルが同じチェックポイントに合格するのに支払った額で、EngineerOSが固定の価格表で測定します。1倍未満なら、この学習者のエージェントはより少ないコストで合格に達し、1倍を超えればより多くかかったことを意味します。最良の結果だけでなく、測定したすべての合格を数えています。コストは学習者の申告ではなく、EngineerOS自身のゲートウェイで数えたものです。',
  },

  study: {
    docTitle: 'エージェントコスト調査 · EngineerOS',
    kicker: '調査',
    title: 'エージェントコスト調査',
    lede: 'EngineerOSで学ぶことで、AIエージェントが正しい結果に達するまでに使う額が変わるのかを調べます。調査の結果は、どのようなものであっても公表します。',
    stepsTitle: '参加すると',
    consentIntro: 'EngineerOSの効率調査。私たちのレッスンによって、AIエージェントがより少ない費用で正しい結果に達するようになるかを測定します。',
    steps: (weeks: number, first: number, last: number, credit: string) => [
      'まず、あなたのEngineerOS AIキーを通じてAIエージェントを使い、4つのタスクのベンチマークを受けます。ベンチマークタスクでのモデル利用料は私たちが負担し、あなたのクレジットからは引かれません。',
      `その後、レベル${num(first, 'ja')}から${num(last, 'ja')}をいつもどおり、最長${num(weeks, 'ja')}週間進めます。その間、一部の参加者には合格ごとにパーと比べたコストと推奨される手法が表示され、一部の参加者には調査の後でのみ表示されます。どちらのグループになるかは無作為に決まり、あなたには示されません。最後には全員が完全なフィードバックを受け取ります。`,
      'その後、似た4つのタスクで2回目のベンチマークを受けます。',
      `両方のベンチマークを終えると、AIクレジット${credit}を受け取ります。ほかのクレジットと同じく、現金としての価値はありません。`,
    ],
    consentTitle: '記録するもの、決してしないこと',
    consent: [
      'モデル呼び出しごとに、モデル、トークン数、コスト、時刻を、取り組んでいたタスクやレベルと結びつけて記録します。また各タスクの結果(合格かどうか)と、かかった時間も記録します。',
      'あなたやエージェントが書いたものは決して記録しません。プロンプトも、回答も、コードも記録しません。',
      '結果は集計した形でのみ公表し、あなたの名前やアカウント、あなたを特定できるものと一緒に公表することはありません。',
      'このページから、理由を告げずにいつでも離脱できます。その場合、調査の結果は削除されて分析から除外されます。受け取ったクレジットを含め、それ以外のものはすべてそのまま残ります。',
    ],
    agree: '18歳以上であり、内容を読み、参加に同意します。',
    join: '調査に参加する',
    joining: '参加手続き中…',
    signIn: '調査に参加するにはサインインしてください。',
    signInButton: 'サインイン',
    loading: '読み込み中…',
    unreachable: '今は調査に接続できませんでした。少ししてからもう一度お試しください。',
    failed: 'うまくいきませんでした。もう一度お試しください。',
    refusals: {
      closed: '現在、調査は新しい参加者を受け付けていません。',
      'no-ai-key': '参加するには、まずEngineerOS AIキーを作成してください。測定されるのは、このキーを通る呼び出しだけです。',
      'no-course': '調査のレベルには有料のものがあるため、現在の期間のコースが必要です。',
      'no-ai-credits': 'ここではAIクレジットが有効になっていないため、調査はまだ何も測定できません。',
      enrolled: 'すでに調査に参加しています。離脱した人は再び参加できません。',
      consent: '同意書の内容が変わりました。ページを再読み込みし、もう一度読んで新しい版に同意してください。',
    },

    benchTitle: (n: number) => `ベンチマーク${num(n, 'ja')}`,
    benchIntro: '4つのタスクを、それぞれエージェントが好きな順番で行います。各タスクの挑戦は1回だけで、最初に終わった試行が結果になります。',
    kinds: ['報告からバグを直す', 'テスト付きで機能を追加する', '動作を変えずにリファクタリングする', '壊れたサービスをデバッグする'],
    taskId: 'タスクID',
    start: '開始',
    resume: '続ける',
    states: { todo: '未着手', open: '進行中', passed: '合格', failed: '不合格', capped: '上限で停止' },
    howTitle: 'エージェントでタスクを始める方法',
    how: (connect: string, check: string) => [
      'エージェントキーで、エージェントをEngineerOSに一度つなぎます。',
      `タブでコースを開き、「${connect}」を押します。`,
      'タスクの横に表示されたタスクIDを使って、open_levelを呼び出すようエージェントに頼みます。',
      `エージェントは「${check}」が通るまで作業します。手作業で解いたタスクはベンチマークに数えません。`,
    ],
    limit: (minutes: number) =>
      `タスクは${num(minutes, 'ja')}分を過ぎるか、コストの上限に達すると数えられなくなります。どちらも不合格として扱います。`,
    funded: 'ベンチマークのモデル呼び出しの費用はEngineerOSが負担し、あなたのクレジットからは引かれません。',
    openCourse: 'コースを開く',
    setUp: 'エージェントを設定する',

    learningTitle: 'ベンチマーク1が終わりました',
    learning: (first: number, last: number, date: string) =>
      `いつもどおりコースを進めてください。ベンチマーク2は、レベル${num(first, 'ja')}から${num(last, 'ja')}を終えたとき、または${date}のどちらか早いほうに始まります。`,
    learningNoDate: (first: number, last: number, days: number) =>
      `いつもどおりコースを進めてください。ベンチマーク2は、レベル${num(first, 'ja')}から${num(last, 'ja')}を終えたとき、またはベンチマーク1の${num(days, 'ja')}日後のどちらか早いほうに始まります。`,
    doneTitle: '調査が完了しました',
    done: (credit: string) => `ありがとうございました。AIクレジット${credit}が残高に入っています。`,
    doneNoCredit: 'ありがとうございました。AIクレジットはまもなく残高に入ります。',
    results: '結果は、調査の終了時に集計した形で公表します。',
    withdrawnTitle: '調査から離脱しました',
    withdrawn: '調査の結果は削除され、分析から除外されました。受け取ったクレジットを含め、それ以外のものはすべてそのまま残ります。',
    leave: '調査から離脱する',
    leaveConfirm: '調査から離脱しますか? 調査の結果は削除されて分析から除外され、再び参加することはできません。受け取ったクレジットを含め、それ以外のものはそのまま残ります。',
    leaveYes: 'はい、離脱します',
    stay: '残る',
    date: (at: number) => calendarDate(new Date(at), 'ja'),
  },
};
